import React from 'react';

import { useClaim } from '~/components/ClaimContainer';
import { getRoleDescription } from '~/components/communications/ContactUtils';
import { ContactEntity } from '~/components/Contact';
import InvolvedLinks from '~/components/Contacts/ContactMiniCard/InvolvedLinks';
import RepresentativeLink from '~/components/Contacts/ContactMiniCard/RepresentativeLink';
import Text from '~/components/core/TextComponents/Text';
import { useCms } from '~/components/hooks/useCms';
import useOrganization from '~/components/OrganizationContext';
import Divider from '~/components/ui/atoms/Divider';
import { isDatesDiffLessThan18Years, isoDateToUs } from '~/DateTimeUtils';
import LEGACY_ORG_FEATURES from '~/server_shared/generated-types/LEGACY_ORG_FEATURES';
import MEDICAL_BILL_PROVIDER_PROFESSIONS from '~/server_shared/generated-types/MEDICAL_BILL_PROVIDER_PROFESSIONS';
import type { ContactFullModel } from '~/server_shared/swagger/data-contracts';
import { isLegacyOrgFeatureEnabled } from '~/Utils';

interface IdentitySubHeaderPropsType {
  contact: ContactFullModel;
}

const IdentitySubHeader: React.FC<IdentitySubHeaderPropsType> = ({ contact }) => {
  const { claim } = useClaim();
  // eslint-disable-next-line @typescript-eslint/ban-ts-comment
  // @ts-ignore
  const { organizationContactRolesDict } = useOrganization();
  const { userOrganization } = useCms();
  const contactRoleDesc = getRoleDescription(contact, organizationContactRolesDict);

  return (
    <div>
      {!contact.is_company && (contact.sex || contact.date_of_birth) ? (
        <div className="flex">
          <Text variant={Text.VARIANTS.SM} weight={Text.WEIGHTS.REGULAR} colorVariant={Text.COLOR_VARIANTS.SECONDARY}>
            {contact.sex || ''}
          </Text>
          {contact.date_of_birth ? (
            <>
              {contact.sex ? <Divider orientation="vertical" variant="middle" flexItem className="mx-8" /> : null}
              <Text
                variant={Text.VARIANTS.SM}
                weight={Text.WEIGHTS.REGULAR}
                colorVariant={Text.COLOR_VARIANTS.SECONDARY}
              >
                {isoDateToUs(contact.date_of_birth)}
              </Text>
              {claim && isDatesDiffLessThan18Years(contact.date_of_birth, claim?.incident?.date_of_loss) ? (
                <>
                  <Divider orientation="vertical" variant="middle" flexItem className="mx-8" />
                  <Text variant={Text.VARIANTS.SM} colorVariant={Text.COLOR_VARIANTS.ERROR}>
                    Minor
                  </Text>
                </>
              ) : null}
            </>
          ) : null}
        </div>
      ) : null}

      <div className="flex items-center">
        <Text variant={Text.VARIANTS.SM} weight={Text.WEIGHTS.REGULAR} colorVariant={Text.COLOR_VARIANTS.SECONDARY}>
          {contactRoleDesc}
        </Text>
        {isLegacyOrgFeatureEnabled(userOrganization, LEGACY_ORG_FEATURES.MGM_MEDICAL_BILL_PROVIDER_PROFESSION) &&
          contact.contact_extra?.profession &&
          contact.contact_extra.profession in MEDICAL_BILL_PROVIDER_PROFESSIONS && (
            <>
              <Divider orientation="vertical" variant="middle" flexItem className="mx-8" />
              <Text
                variant={Text.VARIANTS.SM}
                weight={Text.WEIGHTS.REGULAR}
                colorVariant={Text.COLOR_VARIANTS.SECONDARY}
              >
                {
                  MEDICAL_BILL_PROVIDER_PROFESSIONS[
                    contact.contact_extra.profession as keyof typeof MEDICAL_BILL_PROVIDER_PROFESSIONS
                  ].desc
                }
              </Text>
            </>
          )}
        {contact.is_deactivated && contact.reference_contact_id ? (
          <>
            <Divider orientation="vertical" variant="middle" flexItem className="mx-8" />
            <Text variant={Text.VARIANTS.SM} colorVariant={Text.COLOR_VARIANTS.SECONDARY} className="mr-4">
              Replaced by
            </Text>
            <ContactEntity
              contactId={contact.reference_contact_id}
              contactDisplayName={contact.reference_contact_full_name || 'View contact'}
            />
          </>
        ) : null}
      </div>
      {claim && (
        <>
          <RepresentativeLink contact={contact} />
          <InvolvedLinks contact={contact} />
        </>
      )}
    </div>
  );
};

export default IdentitySubHeader;
